import type { AxiosRequestConfig } from "axios";
import qs from "qs";
import { axiosInstance } from "../../axios-instance";
import { qsOptions } from "../qsOptions";
import type { R } from "../types";
import type {
  IDeleteInspectorRequest,
  IDeleteInspectorResponse,
  IGetHudDetailsResponse,
  IGetInspectorProfileByUserIdRequest,
  IGetInspectorProfileByUserIdResponse,
  IGetInspectorsRequest,
  IGetInspectorsResponse,
  IGetLicenseDetailsResponse,
  IGetW9DetailsResponse,
  IUpdateInspectorRequest,
  IUpdateInspectorResponse,
  IUpdateInspectorServiceAreasRequest,
  IUpdateInspectorServiceAreasResponse,
  IUpsertHudDetailsResponse,
  IUpsertLicenseDetailsResponse,
  IUpsertW9DetailsResponse,
  IValidateHudIdRequest,
  IValidateHudIdResponse,
  IValidateLicenseRequest,
  IValidateLicenseResponse,
  IValidateW9FormRequest,
  IValidateW9FormResponse,
} from "./types";

export class InspectorsService {
  async getAll(
    p: IGetInspectorsRequest,
    c?: AxiosRequestConfig,
  ): R<IGetInspectorsResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.get(`/api/inspector/all?${params}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async deleteInspector(
    p: IDeleteInspectorRequest,
    c?: AxiosRequestConfig,
  ): R<IDeleteInspectorResponse> {
    try {
      return axiosInstance.delete(`/api/inspector/${p.userId}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateInspector(
    p: IUpdateInspectorRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateInspectorResponse> {
    try {
      return axiosInstance.put(`/api/inspector`, p, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async upsertLicenseDetails(
    p: FormData,
    c?: AxiosRequestConfig,
  ): R<IUpsertLicenseDetailsResponse> {
    try {
      return axiosInstance.post("/api/inspector/license", p, {
        ...c,
        headers: {
          ...c?.headers,
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async validateLicenseDetails(
    p: IValidateLicenseRequest,
    c?: AxiosRequestConfig,
  ): R<IValidateLicenseResponse> {
    try {
      const params = qs.stringify({ side: p.side }, qsOptions);
      return axiosInstance.post(
        `/api/inspector/license/validate?${params}`,
        p.formData,
        {
          ...c,
          headers: {
            ...c?.headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async upsertHudDetails(
    p: FormData,
    c?: AxiosRequestConfig,
  ): R<IUpsertHudDetailsResponse> {
    try {
      return axiosInstance.post("/api/inspector/hudDetail", p, {
        ...c,
        headers: {
          ...c?.headers,
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async validateHudDetails(
    p: IValidateHudIdRequest,
    c?: AxiosRequestConfig,
  ): R<IValidateHudIdResponse> {
    try {
      const params = qs.stringify({ side: p.side }, qsOptions);
      return axiosInstance.post(
        `/api/inspector/hudDetail/validate?${params}`,
        p.formData,
        {
          ...c,
          headers: {
            ...c?.headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async upsertW9Details(
    p: FormData,
    c?: AxiosRequestConfig,
  ): R<IUpsertW9DetailsResponse> {
    try {
      return axiosInstance.post("/api/inspector/w9form", p, {
        ...c,
        headers: {
          ...c?.headers,
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async validateW9Details(
    p: IValidateW9FormRequest,
    c?: AxiosRequestConfig,
  ): R<IValidateW9FormResponse> {
    try {
      const params = qs.stringify({ side: p.side }, qsOptions);
      return axiosInstance.post(
        `/api/inspector/w9form/validate?${params}`,
        p.formData,
        {
          ...c,
          headers: {
            ...c?.headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getProfileByUserId(
    p: IGetInspectorProfileByUserIdRequest,
    c?: AxiosRequestConfig,
  ): R<IGetInspectorProfileByUserIdResponse> {
    try {
      return axiosInstance.get(`/api/inspector/user/${p.userId}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getW9Details(c?: AxiosRequestConfig): R<IGetW9DetailsResponse> {
    try {
      return axiosInstance.get("/api/inspector/w9form", c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getLicenseDetails(
    c?: AxiosRequestConfig,
  ): R<IGetLicenseDetailsResponse> {
    try {
      return axiosInstance.get("/api/inspector/license", c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getHudDetails(c?: AxiosRequestConfig): R<IGetHudDetailsResponse> {
    try {
      return axiosInstance.get("/api/inspector/hudDetail", c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateInspectorServiceArea(
    p: IUpdateInspectorServiceAreasRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateInspectorServiceAreasResponse> {
    try {
      return axiosInstance.put(
        `/api/inspector/updateServiceArea/${p.userId}`,
        p.serviceAreas,
        c,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
